import {
  ProjectManagementProjectDetailsPageDocument,
  ProjectManagementProjectDetailsPageSettingsBudgetSectionCreateBudgetStrategyDocument,
  ProjectManagementProjectDetailsPageSettingsBudgetSectionCreateBudgetStrategyMutation,
  ProjectManagementProjectDetailsPageSettingsBudgetSectionDeleteBudgetStrategyDocument,
  ProjectManagementProjectDetailsPageSettingsBudgetSectionDeleteBudgetStrategyMutation,
  ProjectManagementProjectDetailsPageSettingsBudgetSectionUpdateBudgetStrategyDocument,
  ProjectManagementProjectDetailsPageSettingsBudgetSectionUpdateBudgetStrategyMutation,
  ProjectManagementProjectDetailsPageSettingsPageDocument,
  ProjectManagementValueObjectsBudgetStrategyTypeEnum,
  SubprojectSidepanelDetailsDocument,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'

interface createBudgetStrategyDto {
  budgetStrategyType: ProjectManagementValueObjectsBudgetStrategyTypeEnum
  fixedCostCents?: number
  feeAmountCents?: number
  isBillable?: boolean
  projectId: number
  plannedCents?: number
  plannedMinutes?: number
  delegated: boolean
  subprojectId?: number
}
interface updateBudgetStrategyDto {
  id: number
  budgetStrategyType: ProjectManagementValueObjectsBudgetStrategyTypeEnum
  feeAmountCents?: number
  fixedCostCents?: number
  isBillable?: boolean
  plannedCents?: number
  plannedMinutes?: number
}

export const useCreateBudgetStrategy = (companyId: number) => {
  const [createBudgetStrategy] = useMutation(
    ProjectManagementProjectDetailsPageSettingsBudgetSectionCreateBudgetStrategyDocument,
    {
      extractorFn: (
        data: ProjectManagementProjectDetailsPageSettingsBudgetSectionCreateBudgetStrategyMutation
      ) => data.projectManagementMutations.createProjectmanagementBudgetStrategy,
    }
  )

  return {
    createBudgetStrategy(dto: createBudgetStrategyDto, awaitRefetchQueries = false) {
      const {
        budgetStrategyType,
        feeAmountCents,
        fixedCostCents,
        isBillable,
        plannedCents,
        plannedMinutes,
        projectId,
        delegated,
        subprojectId,
      } = dto
      return createBudgetStrategy({
        variables: {
          budgetStrategyType,
          feeAmountCents,
          fixedCostCents,
          isBillable,
          plannedCents,
          plannedMinutes,
          projectId,
          delegated,
          subprojectId,
        },
        awaitRefetchQueries: awaitRefetchQueries,
        refetchQueries: [
          {
            query: ProjectManagementProjectDetailsPageSettingsPageDocument,
            variables: {
              companyId,
              projectId,
              projectBudgetIsEnabled: true,
              projectCostsIsEnabled: true,
            },
          },
          SubprojectSidepanelDetailsDocument,
          ProjectManagementProjectDetailsPageDocument,
        ],
      })
    },
  }
}

export const useUpdateBudgetStrategy = () => {
  const [updateBudgetStrategy] = useMutation(
    ProjectManagementProjectDetailsPageSettingsBudgetSectionUpdateBudgetStrategyDocument,
    {
      extractorFn: (
        data: ProjectManagementProjectDetailsPageSettingsBudgetSectionUpdateBudgetStrategyMutation
      ) => data.projectManagementMutations.updateProjectmanagementBudgetStrategy,
    }
  )

  return {
    updateBudgetStrategy(dto: updateBudgetStrategyDto, awaitRefetchQueries = false) {
      const {
        id,
        budgetStrategyType,
        feeAmountCents,
        fixedCostCents,
        isBillable,
        plannedCents,
        plannedMinutes,
      } = dto
      return updateBudgetStrategy({
        variables: {
          id,
          budgetStrategyType,
          feeAmountCents,
          fixedCostCents,
          isBillable,
          plannedCents,
          plannedMinutes,
        },
        awaitRefetchQueries: awaitRefetchQueries,
        refetchQueries: [
          ProjectManagementProjectDetailsPageSettingsPageDocument,
          SubprojectSidepanelDetailsDocument,
          ProjectManagementProjectDetailsPageDocument,
        ],
        optimisticResponse: {
          projectManagementMutations: {
            updateProjectmanagementBudgetStrategy: {
              errors: [],
              budgetStrategy: {
                __typename: 'ProjectManagementBudgetStrategy',
                id,
                budgetType: budgetStrategyType,
                feeAmountCents: feeAmountCents ?? null,
                plannedCents: plannedCents ?? null,
                plannedMinutes: plannedMinutes ?? null,
              },
            },
          },
        },
      })
    },
  }
}

export const useDeleteBudgetStrategy = () => {
  const [deleteBudgetStrategy] = useMutation(
    ProjectManagementProjectDetailsPageSettingsBudgetSectionDeleteBudgetStrategyDocument,
    {
      extractorFn: (
        data: ProjectManagementProjectDetailsPageSettingsBudgetSectionDeleteBudgetStrategyMutation
      ) => data.projectManagementMutations.deleteProjectmanagementBudgetStrategy,
    }
  )

  return {
    deleteBudgetStrategy({ id }: { id: number }) {
      return deleteBudgetStrategy({
        variables: { id },
        refetchQueries: [ProjectManagementProjectDetailsPageDocument],
        update(cache, mutationResult) {
          const deletedBudgetStrategy =
            mutationResult.data?.projectManagementMutations.deleteProjectmanagementBudgetStrategy
          if (deletedBudgetStrategy) {
            cache.evict({
              id: cache.identify({
                id: id,
                __typename: 'ProjectManagementBudgetStrategy',
              }),
            })
          }
        },
      })
    },
  }
}
